import { inject, Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { SystemService } from 'src/app/system/services/system.service';
import { CurrentUserService } from '../../user/services';

@Injectable({
    providedIn: 'root'
})
export class ApplicationInsightsService {
    private systemService = inject(SystemService);
    private userService = inject(CurrentUserService);

    private appInsights: ApplicationInsights = null;

    constructor() {
        void this.systemService.getAppInsightsInfo().then(info => {
            if (info?.ConnectionString) {
                this.appInsights = new ApplicationInsights({
                    config: {
                        connectionString: info.ConnectionString,
                        enableAutoRouteTracking: true,
                        autoTrackPageVisitTime: true,
                        enableRequestHeaderTracking: true,
                        enableResponseHeaderTracking: true,
                        disableExceptionTracking: false,
                        disableTelemetry: false,
                        enableUnhandledPromiseRejectionTracking: true,
                    }
                });

                this.appInsights.addTelemetryInitializer(item => {
                    item.data['LinkTenantName'] = info.TenantName;
                    item.data['LinkEnvironment'] = info.EnvironmentName;
                    item.data['LinkHostName'] = 'Browser';
                });

                this.setCurrentUser();
                this.appInsights.loadAppInsights();
            }
        });

        this.userService.userChangedObserverSubject.subscribe(() => {
            this.setCurrentUser();
        });
    }

    private setCurrentUser() {
        if (this.appInsights && this.userService.hasCurrentUser()) {
            const currentUser = this.userService.getCurrentUser();
            this.appInsights.setAuthenticatedUserContext(currentUser.UserId.toString(), '', true);
        }
        else if (this.appInsights) {
            this.appInsights.clearAuthenticatedUserContext();
        }
    }

    logPageView(name?: string, url?: string) {
        this.appInsights?.trackPageView({ name: name, uri: url });
    }

    logEvent(name: string, properties?: { [key: string]: unknown }) {
        this.appInsights?.trackEvent({ name: name }, properties);
    }

    logMetric(name: string, average: number, properties?: { [key: string]: unknown }) {
        this.appInsights?.trackMetric({ name: name, average: average }, properties);
    }

    logException(exception: Error, severityLevel?: number) {
        this.appInsights?.trackException({ exception: exception, severityLevel: severityLevel });
    }

    logTrace(message: string, properties?: { [key: string]: unknown }) {
        this.appInsights?.trackTrace({ message: message }, properties);
    }
}
